// File: frontend/src/components/admin/AdminMasterData.tsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus, FaEdit, FaToggleOn, FaToggleOff, FaGlobe, FaTrophy, FaCalendarAlt, FaPencilAlt, FaHashtag, FaTrash } from "react-icons/fa";
import { GiWhistle } from "react-icons/gi";
import {
  MdCountryDto,
  MdLeagueDto,
  MdRoundDto,
  MdPersonRoleDto,
  SaveMdCountryRequest,
  SaveMdLeagueRequest,
  SaveMdRoundRequest,
  SaveMdPersonRoleRequest,
  adminGetCountries,
  adminGetLeagues,
  adminGetRounds,
  adminGetPersonRoles,
  adminCreateCountry,
  adminUpdateCountry,
  adminToggleCountry,
  adminCreateLeague,
  adminUpdateLeague,
  adminToggleLeague,
  adminCreateRound,
  adminUpdateRound,
  adminToggleRound,
  adminCreatePersonRole,
  adminUpdatePersonRole,
  adminTogglePersonRole,
  adminRenamePersonRoleCategory,
  MdTopicCategoryDto,
  SaveMdTopicCategoryRequest,
  adminGetTopicCategories,
  adminCreateTopicCategory,
  adminUpdateTopicCategory,
  adminToggleTopicCategory,
  adminGetTopicCategoryTopicCount,
  adminDeleteTopicCategory,
} from "../../services/masterDataService";
import {
  TopicCategoryIconByKey,
  TOPIC_CATEGORY_ICON_GROUPS,
  TOPIC_CATEGORY_SUGGESTED_ICON,
} from "../../utils/topicCategoryIconMap";
import { RoleIconByKey, ROLE_ICON_KEYS } from "../../utils/roleIconMap";
import {
  ADMIN_MD_CODE_MAX_LENGTH,
  ADMIN_MD_FLAG_MAX_LENGTH,
  ADMIN_MD_NAME_MAX_LENGTH,
  ADMIN_MD_COUNTRY_NAME_MAX_LENGTH,
  ADMIN_MD_ROLE_NAME_MAX_LENGTH,
  ADMIN_MD_CATEGORY_NAME_MAX_LENGTH,
  ADMIN_MD_ROLE_CODE_MAX_LENGTH,
  ADMIN_MD_ROLE_CATEGORY_MAX_LENGTH,
  ADMIN_MD_ICON_KEY_MAX_LENGTH,
} from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { AdminCharCounter } from "./common";
import { Pagination } from "../../utils/Pagination";
import { invalidatePersonRoleCache } from "../../hooks/usePersonRoles";
import { AdminErrorBanner, AdminSuccessBanner } from "../common/AdminBanner";
import { ConfirmModal } from "../common/ConfirmModal";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
// Number input with custom, always-visible up/down arrows (native spinner is unreliable)
import { InputClearButton } from "../common/InputClearButton";
import NumberStepper from "./NumberStepper";
import "./AdminMasterData.css";

interface AdminMasterDataProps {
  isAdmin: boolean;
}

// Sport type options (must match backend SportType enum)
const SPORT_TYPES = [
  "FOOTBALL", "BASKETBALL", "VOLLEYBALL",
  "AMERICAN_FOOTBALL", "BASEBALL", "ICE_HOCKEY", "HANDBALL",
];

type MdTab = "countries" | "leagues" | "rounds" | "roles" | "topicCategories";

/**
 * Canonical form of a role code: upper case, inner spaces turned into underscores.
 * The code is a machine key stored in person_career_entries.role, so it stays upper
 * case — unlike the category, which is a display label and keeps the typed casing.
 * The backend applies the same normalization, so both ends agree on the stored value.
 */
const normalizeRoleCode = (raw: string): string =>
  raw.replace(/\s+/g, "_").toUpperCase();

// Client-side page size for the channel ("kanal") master data list
const TOPIC_CATEGORY_PAGE_SIZE = 20;

// ── Form state types ──────────────────────────────────────────────────────────

interface CountryFormState {
  code: string;
  displayName: string;
  flagEmoji: string;
  displayOrder: number;
}

interface LeagueFormState {
  name: string;
  sportType: string;
  countryCode: string;
  countryFlag: string;
  priority: boolean;
  displayOrder: number;
}

interface RoundFormState {
  name: string;
  sportType: string;
  displayOrder: number;
}

interface RoleFormState {
  code: string;
  nameTr: string;
  nameEn: string;
  iconKey: string;
  category: string;
  // CLUB | ORGANIZATION | NONE — decides what the career-entry form asks for
  affiliationType: string;
  sortOrder: number;
}

const emptyCountryForm = (): CountryFormState => ({ code: "", displayName: "", flagEmoji: "", displayOrder: 0 });

interface TopicCategoryFormState {
  name: string;
  displayOrder: number;
  // Optional react-icons key — empty string means "no icon"
  iconKey: string;
}
const emptyTopicCategoryForm = (): TopicCategoryFormState => ({ name: "", displayOrder: 0, iconKey: "" });
const emptyLeagueForm  = (): LeagueFormState  => ({ name: "", sportType: "FOOTBALL", countryCode: "INT", countryFlag: "", priority: false, displayOrder: 0 });
const emptyRoundForm   = (): RoundFormState   => ({ name: "", sportType: "", displayOrder: 0 });
const emptyRoleForm    = (): RoleFormState    => ({ code: "", nameTr: "", nameEn: "", iconKey: "FaUser", category: "PLAYER", affiliationType: "CLUB", sortOrder: 0 });

// Affiliation options offered in the role modal — mirrors md_person_role.affiliation_type
const ROLE_AFFILIATION_TYPES = ["CLUB", "ORGANIZATION", "NONE"];

// ── Main component ────────────────────────────────────────────────────────────

export const AdminMasterData: React.FC<AdminMasterDataProps> = ({ isAdmin }) => {
  const { t } = useTranslation();

  // Sub-tab
  const [activeTab, setActiveTab] = useState<MdTab>("countries");

  // Loading / saving
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);

  // Auto-dismissing banners
  const [success, setSuccess, clearSuccess] = useAutoDismiss();
  const [error,   setError]   = useAutoDismiss();

  // Data lists
  const [countries, setCountries] = useState<MdCountryDto[]>([]);
  const [topicCategories, setTopicCategories] = useState<MdTopicCategoryDto[]>([]);
  const [leagues,   setLeagues]   = useState<MdLeagueDto[]>([]);
  const [rounds,    setRounds]    = useState<MdRoundDto[]>([]);
  const [roles,     setRoles]     = useState<MdPersonRoleDto[]>([]);

  // Filters
  const [countrySearch, setCountrySearch] = useState("");
  const [topicCategorySearch, setTopicCategorySearch] = useState("");
  // Channel list pagination (client-side, 1-based for the Pagination component) —
  // the list can grow long now that users can propose new channels
  const [topicCategoryPage, setTopicCategoryPage] = useState(1);
  const [leagueSearch,  setLeagueSearch]  = useState("");
  const [leagueSport,   setLeagueSport]   = useState("");
  const [roundSearch,   setRoundSearch]   = useState("");
  const [roundSport,    setRoundSport]    = useState("");
  const [roleSearch,    setRoleSearch]    = useState("");
  const [roleCategory,  setRoleCategory]  = useState("");

  // Modal state
  const [countryModal, setCountryModal] = useState<{ open: boolean; editing: MdCountryDto | null }>({ open: false, editing: null });
  const [topicCategoryModal, setTopicCategoryModal] = useState<{ open: boolean; editing: MdTopicCategoryDto | null }>({ open: false, editing: null });
  const [leagueModal,  setLeagueModal]  = useState<{ open: boolean; editing: MdLeagueDto  | null }>({ open: false, editing: null });
  const [roundModal,   setRoundModal]   = useState<{ open: boolean; editing: MdRoundDto   | null }>({ open: false, editing: null });
  const [roleModal,    setRoleModal]    = useState<{ open: boolean; editing: MdPersonRoleDto | null }>({ open: false, editing: null });

  // Icon picker visibility (inside role modal)
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  // Icon picker visibility (inside topic category / "kanal" modal) — kept separate
  // from the role icon picker above so the two modals never interfere with each other
  const [catIconPickerOpen, setCatIconPickerOpen] = useState(false);
  const [catIconSearch, setCatIconSearch] = useState("");
  const [renameCatModal, setRenameCatModal] = useState<{ open: boolean; oldName: string } | null>(null);
  const [renameCatValue, setRenameCatValue] = useState("");
  const [iconSearch,     setIconSearch]     = useState("");
  const [customCategory, setCustomCategory] = useState("");

  // Form state
  const [countryForm, setCountryForm] = useState<CountryFormState>(emptyCountryForm());
  const [topicCategoryForm, setTopicCategoryForm] = useState<TopicCategoryFormState>(emptyTopicCategoryForm());
  // Delete-channel confirmation: holds the target row and how many topics are
  // still attached to it (shown in the confirmation message). Null = closed.
  const [topicCategoryDelete, setTopicCategoryDelete] = useState<{ target: MdTopicCategoryDto; count: number } | null>(null);
  const [topicCategoryDeleting, setTopicCategoryDeleting] = useState(false);
  const [leagueForm,  setLeagueForm]  = useState<LeagueFormState>(emptyLeagueForm());
  const [roundForm,   setRoundForm]   = useState<RoundFormState>(emptyRoundForm());
  const [roleForm,    setRoleForm]    = useState<RoleFormState>(emptyRoleForm());

  // ── Load data per tab ───────────────────────────────────────────────────────

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      setCountries(await adminGetCountries());
    } catch {
      setError(t("error_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t, setError]);

  const loadTopicCategories = useCallback(async () => {
    setLoading(true);
    try {
      setTopicCategories(await adminGetTopicCategories());
    } catch {
      setError(t("error_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t, setError]);

  const loadLeagues = useCallback(async () => {
    setLoading(true);
    try {
      setLeagues(await adminGetLeagues());
    } catch {
      setError(t("error_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t, setError]);

  const loadRounds = useCallback(async () => {
    setLoading(true);
    try {
      setRounds(await adminGetRounds());
    } catch {
      setError(t("error_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t, setError]);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      setRoles(await adminGetPersonRoles());
    } catch {
      setError(t("error_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t, setError]);

  useEffect(() => {
    if (activeTab === "countries")       loadCountries();
    if (activeTab === "leagues")         loadLeagues();
    if (activeTab === "rounds")          loadRounds();
    if (activeTab === "roles")           loadRoles();
    if (activeTab === "topicCategories") loadTopicCategories();
  }, [activeTab, loadCountries, loadLeagues, loadRounds, loadRoles, loadTopicCategories]);

  // ── Filtered lists ──────────────────────────────────────────────────────────

  const filteredCountries = useMemo(() =>
    countries.filter(c =>
      !countrySearch ||
      c.code.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.displayName.toLowerCase().includes(countrySearch.toLowerCase())
    ), [countries, countrySearch]);

  const filteredTopicCategories = useMemo(() =>
    topicCategories.filter(c =>
      !topicCategorySearch || c.name.toLowerCase().includes(topicCategorySearch.toLowerCase())
    ), [topicCategories, topicCategorySearch]);

  // Client-side page slice of the filtered channel list
  const topicCategoryTotalPages = Math.max(1, Math.ceil(filteredTopicCategories.length / TOPIC_CATEGORY_PAGE_SIZE));
  const pagedTopicCategories = useMemo(() =>
    filteredTopicCategories.slice(
      (topicCategoryPage - 1) * TOPIC_CATEGORY_PAGE_SIZE,
      topicCategoryPage * TOPIC_CATEGORY_PAGE_SIZE
    ), [filteredTopicCategories, topicCategoryPage]);

  // Searching (or a shrunken list after delete) must never leave the view on a
  // page that no longer exists
  useEffect(() => {
    if (topicCategoryPage > topicCategoryTotalPages) setTopicCategoryPage(topicCategoryTotalPages);
  }, [topicCategoryPage, topicCategoryTotalPages]);

  const filteredLeagues = useMemo(() =>
    leagues.filter(l =>
      (!leagueSport || l.sportType === leagueSport) &&
      (!leagueSearch ||
        l.name.toLowerCase().includes(leagueSearch.toLowerCase()) ||
        l.countryCode.toLowerCase().includes(leagueSearch.toLowerCase()))
    ), [leagues, leagueSport, leagueSearch]);

  const filteredRounds = useMemo(() =>
    rounds.filter(r =>
      (!roundSport ||
        (roundSport === "ALL" ? r.sportType === null : r.sportType === roundSport)) &&
      (!roundSearch || r.name.toLowerCase().includes(roundSearch.toLowerCase()))
    ), [rounds, roundSport, roundSearch]);

  const filteredRoles = useMemo(() =>
    roles.filter(r =>
      (!roleCategory || r.category.trim().toLowerCase() === roleCategory.trim().toLowerCase()) &&
      (!roleSearch ||
        r.code.toLowerCase().includes(roleSearch.toLowerCase()) ||
        r.nameTr.toLowerCase().includes(roleSearch.toLowerCase()) ||
        r.nameEn.toLowerCase().includes(roleSearch.toLowerCase()))
    ), [roles, roleCategory, roleSearch]);

  const filteredIcons = useMemo(() =>
    ROLE_ICON_KEYS.filter(k => !iconSearch || k.toLowerCase().includes(iconSearch.toLowerCase())),
    [iconSearch]);

  // Topic category ("kanal") icon picker — grouped by theme so ~70 icons stay
  // browsable; search filters within each group and hides groups with no matches.
  const filteredCatIconGroups = useMemo(() =>
    TOPIC_CATEGORY_ICON_GROUPS
      .map(g => ({
        label: g.label,
        keys: g.keys.filter(k => !catIconSearch || k.toLowerCase().includes(catIconSearch.toLowerCase())),
      }))
      .filter(g => g.keys.length > 0),
    [catIconSearch]);

  // Unique categories derived from the loaded roles list — dynamic, not hardcoded.
  // Deduplicated case-insensitively so "Teknik Ekip" and "TEKNIK EKIP" collapse into a
  // single option (the first spelling wins) instead of showing up as two categories.
  const uniqueCategories = useMemo(() => {
    const seen = new Map<string, string>();
    roles.forEach(r => {
      const key = r.category.trim().toLowerCase();
      if (key && !seen.has(key)) seen.set(key, r.category.trim());
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, "tr"));
  }, [roles]);

  // True when the category typed in the form already exists (ignoring case) — decides
  // whether the free-text "new category" input is shown.
  const isKnownCategory = (value: string): boolean =>
    uniqueCategories.some(c => c.toLowerCase() === value.trim().toLowerCase());

  // ── Country CRUD ────────────────────────────────────────────────────────────

  const openCountryCreate = () => {
    setCountryForm(emptyCountryForm());
    setCountryModal({ open: true, editing: null });
  };

  const openCountryEdit = (c: MdCountryDto) => {
    setCountryForm({ code: c.code, displayName: c.displayName, flagEmoji: c.flagEmoji ?? "", displayOrder: c.displayOrder });
    setCountryModal({ open: true, editing: c });
  };

  const saveCountry = async () => {
    setSaving(true);
    try {
      const req: SaveMdCountryRequest = {
        code: countryForm.code,
        displayName: countryForm.displayName,
        flagEmoji: countryForm.flagEmoji || undefined,
        displayOrder: countryForm.displayOrder,
      };
      if (countryModal.editing) {
        await adminUpdateCountry(countryModal.editing.id, req);
        setSuccess(t("md_country_updated"));
      } else {
        await adminCreateCountry(req);
        setSuccess(t("md_country_created"));
      }
      setCountryModal({ open: false, editing: null });
      loadCountries();
    } catch {
      setError(t("error_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const toggleCountry = async (c: MdCountryDto) => {
    try {
      const updated = await adminToggleCountry(c.id);
      setCountries(prev => prev.map(x => x.id === updated.id ? updated : x));
    } catch {
      setError(t("error_save_failed"));
    }
  };

  // ── Topic category ("konu") CRUD ───────────────────────────────────────────

  const openTopicCategoryCreate = () => {
    setTopicCategoryForm(emptyTopicCategoryForm());
    setCatIconPickerOpen(false);
    setTopicCategoryModal({ open: true, editing: null });
  };

  const openTopicCategoryEdit = (c: MdTopicCategoryDto) => {
    setTopicCategoryForm({ name: c.name, displayOrder: c.displayOrder, iconKey: c.iconKey ?? "" });
    setCatIconPickerOpen(false);
    setTopicCategoryModal({ open: true, editing: c });
  };

  const saveTopicCategory = async () => {
    setSaving(true);
    try {
      const req: SaveMdTopicCategoryRequest = {
        name: topicCategoryForm.name,
        // Only meaningful on update — the backend ignores displayOrder on
        // create and slots the new channel into its alphabetical position
        displayOrder: topicCategoryModal.editing ? topicCategoryForm.displayOrder : 0,
        // Empty string clears the icon on the backend
        iconKey: topicCategoryForm.iconKey || "",
      };
      if (topicCategoryModal.editing) {
        await adminUpdateTopicCategory(topicCategoryModal.editing.id, req);
        setSuccess(t("md_topic_category_updated"));
      } else {
        await adminCreateTopicCategory(req);
        setSuccess(t("md_topic_category_created"));
      }
      setTopicCategoryModal({ open: false, editing: null });
      loadTopicCategories();
    } catch {
      setError(t("error_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const toggleTopicCategory = async (c: MdTopicCategoryDto) => {
    try {
      const updated = await adminToggleTopicCategory(c.id);
      setTopicCategories(prev => prev.map(x => x.id === updated.id ? updated : x));
    } catch {
      setError(t("error_save_failed"));
    }
  };

  // Opens the delete confirmation — the attached-topic count is fetched first
  // so the dialog can warn "bu kanala bağlı N başlık var".
  const askDeleteTopicCategory = async (c: MdTopicCategoryDto) => {
    try {
      const { count } = await adminGetTopicCategoryTopicCount(c.id);
      setTopicCategoryDelete({ target: c, count });
    } catch {
      setError(t("error_save_failed"));
    }
  };

  const confirmDeleteTopicCategory = async () => {
    if (!topicCategoryDelete) return;
    setTopicCategoryDeleting(true);
    try {
      await adminDeleteTopicCategory(topicCategoryDelete.target.id);
      setTopicCategories(prev => prev.filter(x => x.id !== topicCategoryDelete.target.id));
      setSuccess(t("md_topic_category_deleted"));
      setTopicCategoryDelete(null);
    } catch {
      setError(t("error_save_failed"));
    } finally {
      setTopicCategoryDeleting(false);
    }
  };

  // ── League CRUD ─────────────────────────────────────────────────────────────

  const openLeagueCreate = () => {
    setLeagueForm(emptyLeagueForm());
    setLeagueModal({ open: true, editing: null });
  };

  const openLeagueEdit = (l: MdLeagueDto) => {
    setLeagueForm({ name: l.name, sportType: l.sportType, countryCode: l.countryCode, countryFlag: l.countryFlag ?? "", priority: l.priority, displayOrder: l.displayOrder });
    setLeagueModal({ open: true, editing: l });
  };

  const saveLeague = async () => {
    setSaving(true);
    try {
      const req: SaveMdLeagueRequest = {
        name: leagueForm.name,
        sportType: leagueForm.sportType,
        countryCode: leagueForm.countryCode,
        countryFlag: leagueForm.countryFlag || undefined,
        priority: leagueForm.priority,
        displayOrder: leagueForm.displayOrder,
      };
      if (leagueModal.editing) {
        await adminUpdateLeague(leagueModal.editing.id, req);
        setSuccess(t("md_league_updated"));
      } else {
        await adminCreateLeague(req);
        setSuccess(t("md_league_created"));
      }
      setLeagueModal({ open: false, editing: null });
      loadLeagues();
    } catch {
      setError(t("error_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const toggleLeague = async (l: MdLeagueDto) => {
    try {
      const updated = await adminToggleLeague(l.id);
      setLeagues(prev => prev.map(x => x.id === updated.id ? updated : x));
    } catch {
      setError(t("error_save_failed"));
    }
  };

  // ── Round CRUD ──────────────────────────────────────────────────────────────

  const openRoundCreate = () => {
    setRoundForm(emptyRoundForm());
    setRoundModal({ open: true, editing: null });
  };

  const openRoundEdit = (r: MdRoundDto) => {
    setRoundForm({ name: r.name, sportType: r.sportType ?? "", displayOrder: r.displayOrder });
    setRoundModal({ open: true, editing: r });
  };

  const saveRound = async () => {
    setSaving(true);
    try {
      const req: SaveMdRoundRequest = {
        name: roundForm.name,
        sportType: roundForm.sportType || null,
        displayOrder: roundForm.displayOrder,
      };
      if (roundModal.editing) {
        await adminUpdateRound(roundModal.editing.id, req);
        setSuccess(t("md_round_updated"));
      } else {
        await adminCreateRound(req);
        setSuccess(t("md_round_created"));
      }
      setRoundModal({ open: false, editing: null });
      loadRounds();
    } catch {
      setError(t("error_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const toggleRound = async (r: MdRoundDto) => {
    try {
      const updated = await adminToggleRound(r.id);
      setRounds(prev => prev.map(x => x.id === updated.id ? updated : x));
    } catch {
      setError(t("error_save_failed"));
    }
  };

  // ── Role CRUD ───────────────────────────────────────────────────────────────

  const openRoleCreate = () => {
    setRoleForm(emptyRoleForm());
    setIconPickerOpen(false);
    setIconSearch("");
    setCustomCategory("");
    setRoleModal({ open: true, editing: null });
  };

  const openRoleEdit = (r: MdPersonRoleDto) => {
    setRoleForm({
      code: r.code, nameTr: r.nameTr, nameEn: r.nameEn, iconKey: r.iconKey, category: r.category,
      // Rows created before this column existed come back as CLUB from the API
      affiliationType: r.affiliationType ?? "CLUB",
      sortOrder: r.sortOrder,
    });
    setIconPickerOpen(false);
    setIconSearch("");
    // Pre-fill customCategory when the role's category is not in the known list
    // so the free-text input shows the existing value and can be edited
    setCustomCategory(isKnownCategory(r.category) ? "" : r.category);
    setRoleModal({ open: true, editing: r });
  };

  const saveRole = async () => {
    setSaving(true);
    try {
      const req: SaveMdPersonRoleRequest = {
        code: roleForm.code,
        nameTr: roleForm.nameTr,
        nameEn: roleForm.nameEn,
        iconKey: roleForm.iconKey,
        category: roleForm.category,
        affiliationType: roleForm.affiliationType,
        sortOrder: roleForm.sortOrder,
      };
      if (roleModal.editing) {
        await adminUpdatePersonRole(roleModal.editing.id, req);
        setSuccess(t("md_role_updated"));
      } else {
        await adminCreatePersonRole(req);
        setSuccess(t("md_role_created"));
      }
      // Invalidate the module-level cache so other components pick up new roles
      invalidatePersonRoleCache();
      setRoleModal({ open: false, editing: null });
      loadRoles();
    } catch {
      setError(t("error_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = async (r: MdPersonRoleDto) => {
    try {
      const updated = await adminTogglePersonRole(r.id);
      invalidatePersonRoleCache();
      setRoles(prev => prev.map(x => x.id === updated.id ? updated : x));
    } catch {
      setError(t("error_save_failed"));
    }
  };

  const openRenameCat = (cat: string) => {
    setRenameCatValue(cat);
    setRenameCatModal({ open: true, oldName: cat });
  };

  const saveRenameCategory = async () => {
    if (!renameCatModal) return;
    const newCat = renameCatValue.trim();
    if (!newCat) return;
    setSaving(true);
    try {
      await adminRenamePersonRoleCategory(renameCatModal.oldName, newCat);
      // Clear module-level cache so usePersonRoles re-fetches everywhere
      invalidatePersonRoleCache();
      // Close modal first
      setRenameCatModal(null);
      // Update filter to new name so the filtered view stays consistent
      setRoleCategory(newCat);
      setSuccess(t("md_category_renamed"));
      // Re-fetch the admin roles list (awaited so state updates after fetch)
      await loadRoles();
    } catch (e) {
      console.error("rename category error:", e);
      setError(t("error_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="admin-md">
      {success && <AdminSuccessBanner message={success} onDismiss={clearSuccess} />}
      {error && <AdminErrorBanner message={error} />}

      {/* Sub-tab bar */}
      <div className="admin-md-tabs">
        <button className={`admin-md-tab ${activeTab === "countries" ? "active" : ""}`}
                onClick={() => setActiveTab("countries")}>
          <FaGlobe /> {t("md_tab_countries")}
        </button>
        <button className={`admin-md-tab ${activeTab === "leagues" ? "active" : ""}`}
                onClick={() => setActiveTab("leagues")}>
          <FaTrophy /> {t("md_tab_leagues")}
        </button>
        <button className={`admin-md-tab ${activeTab === "rounds" ? "active" : ""}`}
                onClick={() => setActiveTab("rounds")}>
          <FaCalendarAlt /> {t("md_tab_rounds")}
        </button>
        <button className={`admin-md-tab ${activeTab === "roles" ? "active" : ""}`}
                onClick={() => setActiveTab("roles")}>
          <GiWhistle /> {t("md_tab_roles")}
        </button>
        <button className={`admin-md-tab ${activeTab === "topicCategories" ? "active" : ""}`}
                onClick={() => setActiveTab("topicCategories")}>
          <FaHashtag /> {t("md_tab_topic_categories")}
        </button>
      </div>

      {/* ── COUNTRIES TAB ── */}
      {activeTab === "countries" && (
        <>
          <div className="admin-md-toolbar">
            <div className="admin-md-search-wrap">
              <input
                className="admin-md-search"
                placeholder={t("md_search_country")}
                value={countrySearch}
                onChange={e => setCountrySearch(e.target.value)}
              />
              {countrySearch !== "" && <InputClearButton onClear={() => setCountrySearch("")} />}
            </div>
            <span className="admin-md-count" title={t("md_count_tooltip")}>{t("md_count_shown", { shown: filteredCountries.length, total: countries.length })}</span>
            {isAdmin && (
              <button className="admin-md-add-btn" onClick={openCountryCreate}>
                <FaPlus /> {t("md_add_country")}
              </button>
            )}
          </div>
          <div className="admin-md-table-wrap">
            <table className="admin-md-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t("md_col_code")}</th>
                  <th>{t("md_col_flag")}</th>
                  <th>{t("md_col_name")}</th>
                  <th>{t("md_col_order")}</th>
                  <th>{t("md_col_status")}</th>
                  {isAdmin && <th>{t("admin_col_actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="admin-md-empty">{t("loading")}</td></tr>
                ) : filteredCountries.length === 0 ? (
                  <tr><td colSpan={7} className="admin-md-empty">{t("no_results")}</td></tr>
                ) : filteredCountries.map(c => (
                  <tr key={c.id} className={!c.active ? "md-row-inactive" : ""}>
                    <td>#{c.id}</td>
                    <td><strong>{c.code}</strong></td>
                    <td>{c.flagEmoji}</td>
                    <td>{c.displayName}</td>
                    <td>{c.displayOrder}</td>
                    <td>
                      {c.active
                        ? <span className="md-badge-active">{t("status_active")}</span>
                        : <span className="md-badge-inactive">{t("status_inactive")}</span>}
                    </td>
                    {isAdmin && (
                      <td>
                        <div className="md-action-btns">
                          <button className="md-btn-edit" onClick={() => openCountryEdit(c)} title={t("edit")}>
                            <FaEdit />
                          </button>
                          <button
                            className={`md-btn-toggle ${c.active ? "is-active" : "is-inactive"}`}
                            onClick={() => toggleCountry(c)}
                            title={c.active ? t("md_deactivate") : t("md_activate")}
                          >
                            {c.active ? <FaToggleOn /> : <FaToggleOff />}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── TOPIC CATEGORIES TAB ("konu" for custom topics) ── */}
      {activeTab === "topicCategories" && (
        <>
          <div className="admin-md-toolbar">
            <div className="admin-md-search-wrap">
              <input
                className="admin-md-search"
                placeholder={t("md_search_topic_category")}
                value={topicCategorySearch}
                onChange={e => { setTopicCategorySearch(e.target.value); setTopicCategoryPage(1); }}
              />
              {topicCategorySearch !== "" && (
                <InputClearButton onClear={() => { setTopicCategorySearch(""); setTopicCategoryPage(1); }} />
              )}
            </div>
            <span className="admin-md-count" title={t("md_count_tooltip")}>{t("md_count_shown", { shown: filteredTopicCategories.length, total: topicCategories.length })}</span>
            {isAdmin && (
              <button className="admin-md-add-btn" onClick={openTopicCategoryCreate}>
                <FaPlus /> {t("md_add_topic_category")}
              </button>
            )}
          </div>
          <div className="admin-md-table-wrap">
            <table className="admin-md-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t("md_col_icon")}</th>
                  <th>{t("md_col_name")}</th>
                  <th>{t("md_col_order")}</th>
                  <th>{t("md_col_status")}</th>
                  {isAdmin && <th>{t("admin_col_actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="admin-md-empty">{t("loading")}</td></tr>
                ) : filteredTopicCategories.length === 0 ? (
                  <tr><td colSpan={6} className="admin-md-empty">{t("no_results")}</td></tr>
                ) : pagedTopicCategories.map(c => (
                  <tr key={c.id} className={!c.active ? "md-row-inactive" : ""}>
                    <td>#{c.id}</td>
                    <td className="md-topic-icon-cell">
                      {/* Blank cell when the category has no icon assigned */}
                      {c.iconKey && (
                        <span className="md-topic-icon-glyph" title={c.iconKey}>
                          <TopicCategoryIconByKey iconKey={c.iconKey} size={16} />
                        </span>
                      )}
                    </td>
                    <td><strong>{c.name}</strong></td>
                    <td>{c.displayOrder}</td>
                    <td>
                      {c.active
                        ? <span className="md-badge-active">{t("status_active")}</span>
                        : <span className="md-badge-inactive">{t("status_inactive")}</span>}
                    </td>
                    {isAdmin && (
                      <td>
                        <div className="md-action-btns">
                          <button className="md-btn-edit" onClick={() => openTopicCategoryEdit(c)} title={t("edit")}>
                            <FaEdit />
                          </button>
                          <button
                            className={`md-btn-toggle ${c.active ? "is-active" : "is-inactive"}`}
                            onClick={() => toggleTopicCategory(c)}
                            title={c.active ? t("md_deactivate") : t("md_activate")}
                          >
                            {c.active ? <FaToggleOn /> : <FaToggleOff />}
                          </button>
                          {/* Hard delete — hidden for the seeded "nickaltı" channel,
                              which is required by the nickaltı system (backend also
                              rejects it with NICKALTI_CHANNEL_PROTECTED). */}
                          {c.name !== "nickaltı" && (
                            <button
                              className="md-btn-delete"
                              onClick={() => askDeleteTopicCategory(c)}
                              title={t("md_delete_topic_category")}
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Client-side pagination — the channel list can grow long */}
          {topicCategoryTotalPages > 1 && (
            <div className="admin-md-pagination">
              <Pagination
                currentPage={topicCategoryPage}
                totalPages={topicCategoryTotalPages}
                totalItems={filteredTopicCategories.length}
                paginate={setTopicCategoryPage}
              />
            </div>
          )}
        </>
      )}

      {/* ── LEAGUES TAB ── */}
      {activeTab === "leagues" && (
        <>
          <div className="admin-md-toolbar">
            <div className="admin-md-search-wrap">
              <input
                className="admin-md-search"
                placeholder={t("md_search_league")}
                value={leagueSearch}
                onChange={e => setLeagueSearch(e.target.value)}
              />
              {leagueSearch !== "" && <InputClearButton onClear={() => setLeagueSearch("")} />}
            </div>
            <select className="admin-md-filter" value={leagueSport} onChange={e => setLeagueSport(e.target.value)}>
              <option value="">{t("md_all_sports")}</option>
              {SPORT_TYPES.map(s => <option key={s} value={s}>{t(`sport_${s.toLowerCase()}`)}</option>)}
            </select>
            <span className="admin-md-count" title={t("md_count_tooltip")}>{t("md_count_shown", { shown: filteredLeagues.length, total: leagues.length })}</span>
            {isAdmin && (
              <button className="admin-md-add-btn" onClick={openLeagueCreate}>
                <FaPlus /> {t("md_add_league")}
              </button>
            )}
          </div>
          <div className="admin-md-table-wrap">
            <table className="admin-md-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t("md_col_name")}</th>
                  <th>{t("md_col_sport")}</th>
                  <th>{t("md_col_country")}</th>
                  <th>{t("md_col_priority")}</th>
                  <th>{t("md_col_order")}</th>
                  <th>{t("md_col_status")}</th>
                  {isAdmin && <th>{t("admin_col_actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="admin-md-empty">{t("loading")}</td></tr>
                ) : filteredLeagues.length === 0 ? (
                  <tr><td colSpan={6} className="admin-md-empty">{t("no_results")}</td></tr>
                ) : filteredLeagues.map(l => (
                  <tr key={l.id} className={!l.active ? "md-row-inactive" : ""}>
                    <td>#{l.id}</td>
                    <td>
                      {l.countryFlag && <span style={{ marginRight: 4 }}>{l.countryFlag}</span>}
                      {l.name}
                      {l.priority && <span className="md-badge-priority">{t("md_priority")}</span>}
                    </td>
                    <td>{t(`sport_${l.sportType.toLowerCase()}`)}</td>
                    <td>{l.countryCode}</td>
                    <td>{l.priority ? "✓" : "—"}</td>
                    <td>{l.displayOrder}</td>
                    <td>
                      {l.active
                        ? <span className="md-badge-active">{t("status_active")}</span>
                        : <span className="md-badge-inactive">{t("status_inactive")}</span>}
                    </td>
                    {isAdmin && (
                      <td>
                        <div className="md-action-btns">
                          <button className="md-btn-edit" onClick={() => openLeagueEdit(l)} title={t("edit")}>
                            <FaEdit />
                          </button>
                          <button
                            className={`md-btn-toggle ${l.active ? "is-active" : "is-inactive"}`}
                            onClick={() => toggleLeague(l)}
                            title={l.active ? t("md_deactivate") : t("md_activate")}
                          >
                            {l.active ? <FaToggleOn /> : <FaToggleOff />}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── ROUNDS TAB ── */}
      {activeTab === "rounds" && (
        <>
          <div className="admin-md-toolbar">
            <div className="admin-md-search-wrap">
              <input
                className="admin-md-search"
                placeholder={t("md_search_round")}
                value={roundSearch}
                onChange={e => setRoundSearch(e.target.value)}
              />
              {roundSearch !== "" && <InputClearButton onClear={() => setRoundSearch("")} />}
            </div>
            <select className="admin-md-filter" value={roundSport} onChange={e => setRoundSport(e.target.value)}>
              <option value="">{t("md_all_sports")}</option>
              <option value="ALL">{t("md_universal_only")}</option>
              {SPORT_TYPES.map(s => <option key={s} value={s}>{t(`sport_${s.toLowerCase()}`)}</option>)}
            </select>
            <span className="admin-md-count" title={t("md_count_tooltip")}>{t("md_count_shown", { shown: filteredRounds.length, total: rounds.length })}</span>
            {isAdmin && (
              <button className="admin-md-add-btn" onClick={openRoundCreate}>
                <FaPlus /> {t("md_add_round")}
              </button>
            )}
          </div>
          <div className="admin-md-table-wrap">
            <table className="admin-md-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t("md_col_name")}</th>
                  <th>{t("md_col_sport")}</th>
                  <th>{t("md_col_order")}</th>
                  <th>{t("md_col_status")}</th>
                  {isAdmin && <th>{t("admin_col_actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="admin-md-empty">{t("loading")}</td></tr>
                ) : filteredRounds.length === 0 ? (
                  <tr><td colSpan={6} className="admin-md-empty">{t("no_results")}</td></tr>
                ) : filteredRounds.map(r => (
                  <tr key={r.id} className={!r.active ? "md-row-inactive" : ""}>
                    <td>#{r.id}</td>
                    <td>{r.name}</td>
                    <td>
                      {r.sportType
                        ? t(`sport_${r.sportType.toLowerCase()}`)
                        : <span style={{ color: "var(--text-muted)" }}>{t("md_all_sports")}</span>}
                    </td>
                    <td>{r.displayOrder}</td>
                    <td>
                      {r.active
                        ? <span className="md-badge-active">{t("status_active")}</span>
                        : <span className="md-badge-inactive">{t("status_inactive")}</span>}
                    </td>
                    {isAdmin && (
                      <td>
                        <div className="md-action-btns">
                          <button className="md-btn-edit" onClick={() => openRoundEdit(r)} title={t("edit")}>
                            <FaEdit />
                          </button>
                          <button
                            className={`md-btn-toggle ${r.active ? "is-active" : "is-inactive"}`}
                            onClick={() => toggleRound(r)}
                            title={r.active ? t("md_deactivate") : t("md_activate")}
                          >
                            {r.active ? <FaToggleOn /> : <FaToggleOff />}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── ROLES TAB ── */}
      {activeTab === "roles" && (
        <>
          <div className="admin-md-toolbar">
            <div className="admin-md-search-wrap">
              <input
                className="admin-md-search"
                placeholder={t("md_search_role")}
                value={roleSearch}
                onChange={e => setRoleSearch(e.target.value)}
              />
              {roleSearch !== "" && <InputClearButton onClear={() => setRoleSearch("")} />}
            </div>
            <span className="admin-md-count" title={t("md_count_tooltip")}>{t("md_count_shown", { shown: filteredRoles.length, total: roles.length })}</span>
            {isAdmin && (
              <button className="admin-md-add-btn" onClick={openRoleCreate}>
                <FaPlus /> {t("md_add_role")}
              </button>
            )}
          </div>
          <div className="admin-md-category-pills">
            <button
              className={`admin-md-pill ${roleCategory === "" ? "active" : ""}`}
              onClick={() => setRoleCategory("")}
            >
              {t("md_all_categories")}
            </button>
            {uniqueCategories.map(c => (
              <span key={c} className="admin-md-pill-wrap">
                <button
                  className={`admin-md-pill ${roleCategory === c ? "active" : ""}`}
                  onClick={() => setRoleCategory(c)}
                >
                  {t(`md_role_cat_${c.toLowerCase()}`, c)}
                </button>
                {isAdmin && (
                  <button
                    className="admin-md-pill-edit"
                    title={t("md_rename_category")}
                    onClick={e => { e.stopPropagation(); openRenameCat(c); }}
                  >
                    <FaPencilAlt size={9} />
                  </button>
                )}
              </span>
            ))}
          </div>
          <div className="admin-md-table-wrap">
            <table className="admin-md-table">
              <thead>
                <tr>
                  <th>{t("md_col_icon")}</th>
                  <th>{t("md_col_code")}</th>
                  <th>{t("md_col_name_tr")}</th>
                  <th>{t("md_col_category")}</th>
                  <th>{t("md_col_order")}</th>
                  <th style={{ position: "sticky", right: 0, background: "var(--bg-secondary)", zIndex: 2 }}>{t("admin_col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="admin-md-empty">{t("loading")}</td></tr>
                ) : filteredRoles.length === 0 ? (
                  <tr><td colSpan={6} className="admin-md-empty">{t("no_results")}</td></tr>
                ) : filteredRoles.map(r => (
                  <tr key={r.id} className={!r.active ? "md-row-inactive" : ""}>
                    <td>
                      <span className="md-role-icon-preview" title={r.iconKey}>
                        <RoleIconByKey iconKey={r.iconKey} size={16} />
                      </span>
                    </td>
                    <td><code className="md-code">{r.code}</code></td>
                    <td>{r.nameTr}</td>
                    <td>
                      <span className="md-badge-category">
                        {t(`md_role_cat_${r.category.toLowerCase()}`, r.category)}
                      </span>
                    </td>
                    <td>{r.sortOrder}</td>
                    <td style={{ position: "sticky", right: 0, background: "var(--bg-entry)", zIndex: 2 }}>
                      <div className="md-action-btns">
                        <button className="md-btn-edit" onClick={() => openRoleEdit(r)} title={t("edit")}>
                          <FaEdit />
                        </button>
                        <button
                          className={`md-btn-toggle ${r.active ? "is-active" : "is-inactive"}`}
                          onClick={() => toggleRole(r)}
                          title={r.active ? t("md_deactivate") : t("md_activate")}
                        >
                          {r.active ? <FaToggleOn /> : <FaToggleOff />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── RENAME CATEGORY MODAL ── */}
      {renameCatModal?.open && (
        <div className="admin-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setRenameCatModal(null); }}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>{t("md_rename_category")}</h3>
              <button className="modal-close-btn" onClick={() => setRenameCatModal(null)}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="md-form-row full">
                <div className="md-form-field">
                  <label>{t("md_rename_category_label", `"${renameCatModal.oldName}" → yeni isim`)}</label>
                  <div className="md-input-wrap">
                    <input
                      value={renameCatValue}
                      onChange={e => setRenameCatValue(e.target.value)}
                      placeholder="TRFTR"
                      maxLength={ADMIN_MD_CATEGORY_NAME_MAX_LENGTH}
                      autoFocus
                    />
                    {renameCatValue && <InputClearButton onClear={() => setRenameCatValue("")} />}
                  </div>
                  <AdminCharCounter value={renameCatValue} max={ADMIN_MD_CATEGORY_NAME_MAX_LENGTH} />
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="md-btn-cancel" onClick={() => setRenameCatModal(null)}>{t("cancel")}</button>
              <button className="md-btn-save" onClick={saveRenameCategory} disabled={saving || !renameCatValue.trim()}>
                {saving ? t("saving") : t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── COUNTRY MODAL ── */}
      {countryModal.open && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setCountryModal({ open: false, editing: null }); }}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>{countryModal.editing ? t("md_edit_country") : t("md_add_country")}</h3>
              <button className="modal-close-btn" onClick={() => setCountryModal({ open: false, editing: null })}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="md-form-row">
                <div className="md-form-field">
                  <label>{t("md_col_code")}</label>
                  <div className="md-input-wrap">
                    <input value={countryForm.code} onChange={e => setCountryForm(p => ({ ...p, code: e.target.value }))} placeholder="TR" maxLength={ADMIN_MD_CODE_MAX_LENGTH} />
                    {countryForm.code !== "" && <InputClearButton onClear={() => setCountryForm(p => ({ ...p, code: "" }))} />}
                  </div>
                  <AdminCharCounter value={countryForm.code} max={ADMIN_MD_CODE_MAX_LENGTH} />
                </div>
                <div className="md-form-field">
                  <label>{t("md_col_flag")}</label>
                  <div className="md-input-wrap">
                    <input value={countryForm.flagEmoji} onChange={e => setCountryForm(p => ({ ...p, flagEmoji: e.target.value }))} placeholder="🇹🇷" maxLength={ADMIN_MD_FLAG_MAX_LENGTH} />
                    {countryForm.flagEmoji !== "" && <InputClearButton onClear={() => setCountryForm(p => ({ ...p, flagEmoji: "" }))} />}
                  </div>
                  <AdminCharCounter value={countryForm.flagEmoji} max={ADMIN_MD_FLAG_MAX_LENGTH} />
                </div>
              </div>
              <div className="md-form-row full">
                <div className="md-form-field">
                  <label>{t("md_col_name")}</label>
                  <div className="md-input-wrap">
                    <input value={countryForm.displayName} onChange={e => setCountryForm(p => ({ ...p, displayName: e.target.value }))} placeholder={t("md_country_name_placeholder")} maxLength={ADMIN_MD_COUNTRY_NAME_MAX_LENGTH} />
                    {countryForm.displayName !== "" && <InputClearButton onClear={() => setCountryForm(p => ({ ...p, displayName: "" }))} />}
                  </div>
                  <AdminCharCounter value={countryForm.displayName} max={ADMIN_MD_COUNTRY_NAME_MAX_LENGTH} />
                </div>
              </div>
              <div className="md-form-row">
                <div className="md-form-field">
                  <label>{t("md_col_order")}</label>
                  <NumberStepper
                    value={countryForm.displayOrder}
                    min={0}
                    emptyValue={0}
                    onChange={next => setCountryForm(p => ({ ...p, displayOrder: next ?? 0 }))}
                  />
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="md-btn-cancel" onClick={() => setCountryModal({ open: false, editing: null })}>{t("cancel")}</button>
              <button className="md-btn-save" onClick={saveCountry} disabled={saving || !countryForm.code || !countryForm.displayName}>
                {saving ? t("saving") : t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TOPIC CATEGORY MODAL ── */}
      {topicCategoryModal.open && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setTopicCategoryModal({ open: false, editing: null }); }}>
          <div className="admin-modal admin-modal--wide">
            <div className="admin-modal-header">
              <h3>{topicCategoryModal.editing ? t("md_edit_topic_category") : t("md_add_topic_category")}</h3>
              <button className="modal-close-btn" onClick={() => setTopicCategoryModal({ open: false, editing: null })}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="md-form-row full">
                <div className="md-form-field">
                  <label>{t("md_col_name")}</label>
                  <div className="md-input-wrap">
                    <input
                    value={topicCategoryForm.name}
                    onChange={e => {
                      // Category names are lowercase-only (sözlük convention)
                      const name = e.target.value.toLocaleLowerCase("tr");
                      setTopicCategoryForm(p => {
                        // Auto-suggest a matching icon for known channel names, but only
                        // while no icon has been picked yet — never overrides a choice
                        const suggested = !p.iconKey ? TOPIC_CATEGORY_SUGGESTED_ICON[name] : undefined;
                        return { ...p, name, iconKey: suggested ?? p.iconKey };
                      });
                    }}
                    placeholder={t("md_topic_category_name_placeholder")}
                    maxLength={ADMIN_MD_CATEGORY_NAME_MAX_LENGTH}
                    />
                    {topicCategoryForm.name !== "" && (
                      <InputClearButton onClear={() => setTopicCategoryForm(p => ({ ...p, name: "" }))} />
                    )}
                  </div>
                  <AdminCharCounter value={topicCategoryForm.name} max={ADMIN_MD_CATEGORY_NAME_MAX_LENGTH} />
                </div>
              </div>
              <div className="md-form-row">
                <div className="md-form-field">
                  <label>{t("md_col_order")}</label>
                  {topicCategoryModal.editing ? (
                    <NumberStepper
                      value={topicCategoryForm.displayOrder}
                      min={0}
                      emptyValue={0}
                      onChange={next => setTopicCategoryForm(p => ({ ...p, displayOrder: next ?? 0 }))}
                    />
                  ) : (
                    /* New channels are always slotted into their alphabetical
                       position by the backend — no manual order on create.
                       Admins can still move the channel afterwards via edit. */
                    <div className="md-order-auto-hint">
                      {t("md_order_auto_alphabetical", "Sıra otomatik belirlenir (alfabetik).")}
                    </div>
                  )}
                </div>
              </div>

              {/* Icon picker — optional, shown next to the category's title in the
                  custom topic ("gündem") left pane when set. No default: an empty
                  iconKey means the row renders with no leading icon at all.
                  Grouped by theme (Eğlence, Spor, ...) so browsing stays easy even
                  as the icon set grows — search filters within groups. */}
              <div className="md-form-row full">
                <div className="md-form-field">
                  <label>{t("md_col_icon")}</label>
                  <div className="md-icon-selector">
                    <button
                      type="button"
                      className="md-icon-preview-btn"
                      onClick={() => { setCatIconPickerOpen(o => !o); setCatIconSearch(""); }}
                    >
                      {topicCategoryForm.iconKey ? (
                        <>
                          <TopicCategoryIconByKey iconKey={topicCategoryForm.iconKey} size={18} />
                          <span className="md-icon-preview-key">{topicCategoryForm.iconKey}</span>
                        </>
                      ) : (
                        <span className="md-icon-preview-key">{t("md_icon_none")}</span>
                      )}
                      <span className="md-icon-picker-chevron">{catIconPickerOpen ? "▲" : "▼"}</span>
                    </button>

                    {/* Clear button — only relevant once an icon has been picked */}
                    {topicCategoryForm.iconKey && (
                      <button
                        type="button"
                        className="md-btn-cancel"
                        onClick={() => setTopicCategoryForm(p => ({ ...p, iconKey: "" }))}
                      >
                        {t("md_icon_clear")}
                      </button>
                    )}

                    {catIconPickerOpen && (
                      <div className="md-icon-picker md-icon-picker--grouped">
                        <div className="md-icon-picker-search-wrap">
                          <input
                            className="md-icon-picker-search"
                            placeholder={t("md_icon_search_placeholder")}
                            value={catIconSearch}
                            onChange={e => setCatIconSearch(e.target.value)}
                            maxLength={ADMIN_MD_ICON_KEY_MAX_LENGTH}
                            autoFocus
                          />
                          {catIconSearch !== "" && <InputClearButton onClear={() => setCatIconSearch("")} />}
                        </div>
                        <AdminCharCounter value={catIconSearch} max={ADMIN_MD_ICON_KEY_MAX_LENGTH} />
                        <div className="md-icon-picker-groups">
                          {filteredCatIconGroups.length === 0 ? (
                            <div className="admin-md-empty">{t("no_results")}</div>
                          ) : filteredCatIconGroups.map(group => (
                            <div key={group.label} className="md-icon-group">
                              <div className="md-icon-group-label">{group.label}</div>
                              <div className="md-icon-grid">
                                {group.keys.map(key => (
                                  <button
                                    key={key}
                                    type="button"
                                    className={`md-icon-cell ${topicCategoryForm.iconKey === key ? "selected" : ""}`}
                                    title={key}
                                    onClick={() => {
                                      setTopicCategoryForm(p => ({ ...p, iconKey: key }));
                                      setCatIconPickerOpen(false);
                                    }}
                                  >
                                    <TopicCategoryIconByKey iconKey={key} size={20} />
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="md-btn-cancel" onClick={() => setTopicCategoryModal({ open: false, editing: null })}>{t("cancel")}</button>
              <button className="md-btn-save" onClick={saveTopicCategory} disabled={saving || !topicCategoryForm.name.trim()}>
                {saving ? t("saving") : t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LEAGUE MODAL ── */}
      {leagueModal.open && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setLeagueModal({ open: false, editing: null }); }}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>{leagueModal.editing ? t("md_edit_league") : t("md_add_league")}</h3>
              <button className="modal-close-btn" onClick={() => setLeagueModal({ open: false, editing: null })}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="md-form-row full">
                <div className="md-form-field">
                  <label>{t("md_col_name")}</label>
                  <div className="md-input-wrap">
                    <input value={leagueForm.name} onChange={e => setLeagueForm(p => ({ ...p, name: e.target.value }))} placeholder="Süper Lig" maxLength={ADMIN_MD_NAME_MAX_LENGTH} />
                    {leagueForm.name !== "" && <InputClearButton onClear={() => setLeagueForm(p => ({ ...p, name: "" }))} />}
                  </div>
                  <AdminCharCounter value={leagueForm.name} max={ADMIN_MD_NAME_MAX_LENGTH} />
                </div>
              </div>
              <div className="md-form-row">
                <div className="md-form-field">
                  <label>{t("md_col_sport")}</label>
                  <select value={leagueForm.sportType} onChange={e => setLeagueForm(p => ({ ...p, sportType: e.target.value }))}>
                    {SPORT_TYPES.map(s => <option key={s} value={s}>{t(`sport_${s.toLowerCase()}`)}</option>)}
                  </select>
                </div>
                <div className="md-form-field">
                  <label>{t("md_col_country")}</label>
                  <div className="md-input-wrap">
                    <input value={leagueForm.countryCode} onChange={e => setLeagueForm(p => ({ ...p, countryCode: e.target.value }))} placeholder="TR / INT" maxLength={ADMIN_MD_CODE_MAX_LENGTH} />
                    {leagueForm.countryCode !== "" && <InputClearButton onClear={() => setLeagueForm(p => ({ ...p, countryCode: "" }))} />}
                  </div>
                  <AdminCharCounter value={leagueForm.countryCode} max={ADMIN_MD_CODE_MAX_LENGTH} />
                </div>
              </div>
              <div className="md-form-row">
                <div className="md-form-field">
                  <label>{t("md_col_flag")}</label>
                  <div className="md-input-wrap">
                    <input value={leagueForm.countryFlag} onChange={e => setLeagueForm(p => ({ ...p, countryFlag: e.target.value }))} placeholder="🇹🇷" maxLength={ADMIN_MD_FLAG_MAX_LENGTH} />
                    {leagueForm.countryFlag !== "" && <InputClearButton onClear={() => setLeagueForm(p => ({ ...p, countryFlag: "" }))} />}
                  </div>
                  <AdminCharCounter value={leagueForm.countryFlag} max={ADMIN_MD_FLAG_MAX_LENGTH} />
                </div>
                <div className="md-form-field">
                  <label>{t("md_col_order")}</label>
                  <NumberStepper
                    value={leagueForm.displayOrder}
                    min={0}
                    emptyValue={0}
                    onChange={next => setLeagueForm(p => ({ ...p, displayOrder: next ?? 0 }))}
                  />
                </div>
              </div>
              <label className="md-form-checkbox">
                <input type="checkbox" checked={leagueForm.priority} onChange={e => setLeagueForm(p => ({ ...p, priority: e.target.checked }))} />
                {t("md_priority_label")}
              </label>
            </div>
            <div className="admin-modal-footer">
              <button className="md-btn-cancel" onClick={() => setLeagueModal({ open: false, editing: null })}>{t("cancel")}</button>
              <button className="md-btn-save" onClick={saveLeague} disabled={saving || !leagueForm.name || !leagueForm.sportType}>
                {saving ? t("saving") : t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ROUND MODAL ── */}
      {roundModal.open && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setRoundModal({ open: false, editing: null }); }}>
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3>{roundModal.editing ? t("md_edit_round") : t("md_add_round")}</h3>
              <button className="modal-close-btn" onClick={() => setRoundModal({ open: false, editing: null })}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="md-form-row full">
                <div className="md-form-field">
                  <label>{t("md_col_name")}</label>
                  <div className="md-input-wrap">
                    <input value={roundForm.name} onChange={e => setRoundForm(p => ({ ...p, name: e.target.value }))} placeholder="Hafta 1" maxLength={ADMIN_MD_NAME_MAX_LENGTH} />
                    {roundForm.name !== "" && <InputClearButton onClear={() => setRoundForm(p => ({ ...p, name: "" }))} />}
                  </div>
                  <AdminCharCounter value={roundForm.name} max={ADMIN_MD_NAME_MAX_LENGTH} />
                </div>
              </div>
              <div className="md-form-row">
                <div className="md-form-field">
                  <label>{t("md_col_sport")} ({t("md_sport_optional")})</label>
                  <select value={roundForm.sportType} onChange={e => setRoundForm(p => ({ ...p, sportType: e.target.value }))}>
                    <option value="">{t("md_all_sports")}</option>
                    {SPORT_TYPES.map(s => <option key={s} value={s}>{t(`sport_${s.toLowerCase()}`)}</option>)}
                  </select>
                </div>
                <div className="md-form-field">
                  <label>{t("md_col_order")}</label>
                  <NumberStepper
                    value={roundForm.displayOrder}
                    min={0}
                    emptyValue={0}
                    onChange={next => setRoundForm(p => ({ ...p, displayOrder: next ?? 0 }))}
                  />
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="md-btn-cancel" onClick={() => setRoundModal({ open: false, editing: null })}>{t("cancel")}</button>
              <button className="md-btn-save" onClick={saveRound} disabled={saving || !roundForm.name}>
                {saving ? t("saving") : t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ROLE MODAL ── */}
      {roleModal.open && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setRoleModal({ open: false, editing: null }); }}>
          <div className="admin-modal admin-modal--wide">
            <div className="admin-modal-header">
              <h3>{roleModal.editing ? t("md_edit_role") : t("md_add_role")}</h3>
              <button className="modal-close-btn" onClick={() => setRoleModal({ open: false, editing: null })}>×</button>
            </div>
            <div className="admin-modal-body">

              {/* Code + Category */}
              <div className="md-form-row">
                <div className="md-form-field">
                  <label>{t("md_col_code")} *</label>
                  <div className="md-input-wrap">
                    <input
                      value={roleForm.code}
                      onChange={e => setRoleForm(p => ({ ...p, code: normalizeRoleCode(e.target.value) }))}
                      placeholder="HEAD_COACH"
                      maxLength={ADMIN_MD_ROLE_CODE_MAX_LENGTH}
                      disabled={!!roleModal.editing}
                      title={roleModal.editing ? t("md_role_code_locked") : undefined}
                    />
                    {roleForm.code && !roleModal.editing && <InputClearButton onClear={() => setRoleForm(p => ({ ...p, code: "" }))} />}
                  </div>
                  <AdminCharCounter value={roleForm.code} max={ADMIN_MD_ROLE_CODE_MAX_LENGTH} />
                </div>
                <div className="md-form-field">
                  <label>{t("md_col_category")} *</label>
                  <select
                    value={isKnownCategory(roleForm.category) ? roleForm.category : "__custom__"}
                    onChange={e => {
                      if (e.target.value !== "__custom__") {
                        setRoleForm(p => ({ ...p, category: e.target.value }));
                        setCustomCategory("");
                      } else {
                        setRoleForm(p => ({ ...p, category: customCategory }));
                      }
                    }}
                  >
                    {uniqueCategories.map(c => (
                      <option key={c} value={c}>{t(`md_role_cat_${c.toLowerCase()}`, c)}</option>
                    ))}
                    <option value="__custom__">{t("md_role_cat_custom", "Yeni kategori...")}</option>
                  </select>
                  {/* Free-text input shown only when "Yeni kategori..." is selected */}
                  {!isKnownCategory(roleForm.category) && (
                    <div className="md-input-wrap">
                      <input
                        value={customCategory}
                        onChange={e => {
                          // Category is a display label, not a machine key — keep the
                          // casing the admin typed instead of forcing upper case.
                          const val = e.target.value;
                          setCustomCategory(val);
                          setRoleForm(p => ({ ...p, category: val }));
                        }}
                        placeholder="Taraftar Lideri"
                        maxLength={ADMIN_MD_ROLE_CATEGORY_MAX_LENGTH}
                        autoFocus
                      />
                      {customCategory !== "" && (
                        <InputClearButton
                          onClear={() => { setCustomCategory(""); setRoleForm(p => ({ ...p, category: "" })); }}
                        />
                      )}
                    </div>
                  )}
                  {!isKnownCategory(roleForm.category) && (
                    <AdminCharCounter value={customCategory} max={ADMIN_MD_ROLE_CATEGORY_MAX_LENGTH} />
                  )}
                </div>
              </div>

              {/* Affiliation type — decides what the career-entry form asks for when this
                  role is picked: a club record, a free-text organization, or nothing. */}
              <div className="md-form-row">
                <div className="md-form-field">
                  <label>{t("md_col_affiliation")} *</label>
                  <select
                    value={roleForm.affiliationType}
                    onChange={e => setRoleForm(p => ({ ...p, affiliationType: e.target.value }))}
                  >
                    {ROLE_AFFILIATION_TYPES.map(a => (
                      <option key={a} value={a}>{t(`md_affiliation_${a.toLowerCase()}`)}</option>
                    ))}
                  </select>
                  <span className="md-form-hint">
                    {t(`md_affiliation_${roleForm.affiliationType.toLowerCase()}_hint`)}
                  </span>
                </div>
              </div>

              {/* Name TR + Name EN */}
              <div className="md-form-row">
                <div className="md-form-field">
                  <label>{t("md_col_name_tr")} *</label>
                  <div className="md-input-wrap">
                    <input value={roleForm.nameTr} onChange={e => setRoleForm(p => ({ ...p, nameTr: e.target.value }))} placeholder="Teknik Direktör" maxLength={ADMIN_MD_ROLE_NAME_MAX_LENGTH} />
                    {roleForm.nameTr !== "" && <InputClearButton onClear={() => setRoleForm(p => ({ ...p, nameTr: "" }))} />}
                  </div>
                  <AdminCharCounter value={roleForm.nameTr} max={ADMIN_MD_ROLE_NAME_MAX_LENGTH} />
                </div>
                <div className="md-form-field">
                  <label>{t("md_col_name_en")} *</label>
                  <div className="md-input-wrap">
                    <input value={roleForm.nameEn} onChange={e => setRoleForm(p => ({ ...p, nameEn: e.target.value }))} placeholder="Head Coach" maxLength={ADMIN_MD_ROLE_NAME_MAX_LENGTH} />
                    {roleForm.nameEn !== "" && <InputClearButton onClear={() => setRoleForm(p => ({ ...p, nameEn: "" }))} />}
                  </div>
                  <AdminCharCounter value={roleForm.nameEn} max={ADMIN_MD_ROLE_NAME_MAX_LENGTH} />
                </div>
              </div>

              {/* Sort order */}
              <div className="md-form-row">
                <div className="md-form-field md-form-field--narrow">
                  <label>{t("md_col_order")}</label>
                  <NumberStepper
                    value={roleForm.sortOrder}
                    min={0}
                    emptyValue={0}
                    onChange={next => setRoleForm(p => ({ ...p, sortOrder: next ?? 0 }))}
                  />
                </div>
              </div>

              {/* Icon picker */}
              <div className="md-form-row full">
                <div className="md-form-field">
                  <label>{t("md_col_icon")} *</label>
                  <div className="md-icon-selector">
                    {/* Current icon preview + toggle button */}
                    <button
                      type="button"
                      className="md-icon-preview-btn"
                      onClick={() => { setIconPickerOpen(o => !o); setIconSearch(""); }}
                    >
                      <RoleIconByKey iconKey={roleForm.iconKey} size={18} />
                      <span className="md-icon-preview-key">{roleForm.iconKey}</span>
                      <span className="md-icon-picker-chevron">{iconPickerOpen ? "▲" : "▼"}</span>
                    </button>

                    {/* Icon grid */}
                    {iconPickerOpen && (
                      <div className="md-icon-picker">
                        <div className="md-icon-picker-search-wrap">
                          <input
                            className="md-icon-picker-search"
                            placeholder={t("md_icon_search_placeholder")}
                            value={iconSearch}
                            onChange={e => setIconSearch(e.target.value)}
                            maxLength={ADMIN_MD_ICON_KEY_MAX_LENGTH}
                            autoFocus
                          />
                          {iconSearch !== "" && <InputClearButton onClear={() => setIconSearch("")} />}
                        </div>
                        <AdminCharCounter value={iconSearch} max={ADMIN_MD_ICON_KEY_MAX_LENGTH} />
                        {/* Empty state — without it a search that matches nothing looks
                            like the icon list failed to load. */}
                        {filteredIcons.length === 0 ? (
                          <div className="admin-md-empty">{t("no_results")}</div>
                        ) : (
                          <div className="md-icon-grid">
                            {filteredIcons.map(key => (
                              <button
                                key={key}
                                type="button"
                                className={`md-icon-cell ${roleForm.iconKey === key ? "selected" : ""}`}
                                title={key}
                                onClick={() => {
                                  setRoleForm(p => ({ ...p, iconKey: key }));
                                  setIconPickerOpen(false);
                                }}
                              >
                                <RoleIconByKey iconKey={key} size={20} />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
            <div className="admin-modal-footer">
              <button className="md-btn-cancel" onClick={() => setRoleModal({ open: false, editing: null })}>{t("cancel")}</button>
              <button
                className="md-btn-save"
                onClick={saveRole}
                disabled={saving || !roleForm.code || !roleForm.nameTr || !roleForm.nameEn || !roleForm.iconKey}
              >
                {saving ? t("saving") : t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete-channel confirmation — message differs depending on whether
          topics are still attached to the channel. Deleting detaches them. */}
      {topicCategoryDelete && (
        <ConfirmModal
          message={
            topicCategoryDelete.count > 0
              ? t("md_topic_category_delete_confirm_linked", {
                  name: topicCategoryDelete.target.name,
                  count: topicCategoryDelete.count,
                })
              : t("md_topic_category_delete_confirm", { name: topicCategoryDelete.target.name })
          }
          confirmLabel={topicCategoryDeleting ? "..." : t("md_delete_topic_category")}
          cancelLabel={t("cancel")}
          onConfirm={confirmDeleteTopicCategory}
          onCancel={() => setTopicCategoryDelete(null)}
          disabled={topicCategoryDeleting}
        />
      )}
    </div>
  );
};